import {useCallback} from 'react';
import toast from 'react-hot-toast';

export const useCallControls = ({
                                    rtcStatus, localStream, remoteAudioRef,
                                    isMuted, setIsMuted, isCallEnding, setIsCallEnding,
                                    isRemoteAudioMuted, setIsRemoteAudioMuted,
                                    otherParticipant, makeCall, endWebRTCCall,
                                    sendMessage, userId, userName
                                }) => {
    const initiateCall = useCallback(async () => {
        if (!otherParticipant) {
            toast.error('No participant to call', {position: 'bottom-center'});
            return;
        }
        if (rtcStatus === 'connected' || rtcStatus === 'connecting') {
            console.log('[Room] Call already active');
            return;
        }
        console.log('[Room] 📞 Initiating call...');
        try {
            await makeCall();
            console.log('[Room] ✅ Call initiated');
            toast.success(`Connected to ${otherParticipant.name}`, {
                id: 'call-connect', position: 'bottom-center'
            });
            sendMessage({
                type: 'call_started',
                user_id: userId,
                user_name: userName,
            });
        } catch (error) {
            console.error('[Room] ❌ Failed to initiate call:', error);
            toast.error('Failed to connect call', {id: 'call-connect', position: 'bottom-center'});
        }
    }, [otherParticipant, makeCall, rtcStatus, sendMessage, userId, userName]);

    const endCall = useCallback(() => {
        if (isCallEnding) {
            console.log('[Room] Call end already in progress');
            return;
        }
        console.log('[Room] 📞 Ending call...');
        setIsCallEnding(true);
        sendMessage({
            type: 'call_ended',
            user_id: userId,
            user_name: userName,
            timestamp: new Date().toISOString()
        });
        endWebRTCCall();
        console.log('[Room] ✅ Call ended');
    }, [isCallEnding, sendMessage, userId, userName, endWebRTCCall, setIsCallEnding]);

    const toggleMute = useCallback(() => {
        const newMutedState = !isMuted;
        setIsMuted(newMutedState);

        if (localStream) {
            localStream.getAudioTracks().forEach((track) => {
                track.enabled = !newMutedState;
                console.log('[Room] Audio track enabled:', track.enabled);
            });
        }

        sendMessage({
            type: 'mute_status',
            user_id: userId,
            user_name: userName,
            is_muted: newMutedState,
        });
        toast.success(newMutedState ? 'Microphone muted' : 'Microphone unmuted', {
            icon: newMutedState ? '🔇' : '🎤',
            duration: 1500,
            position: 'bottom-center',
        });
    }, [isMuted, localStream, sendMessage, userId, userName, setIsMuted]);

    const toggleRemoteAudio = useCallback(() => {
        if (!remoteAudioRef.current) {
            console.log('[Room] No remote audio to mute');
            return;
        }
        const newMutedState = !isRemoteAudioMuted;
        setIsRemoteAudioMuted(newMutedState);
        const audioElement = remoteAudioRef.current;
        if (audioElement) {
            audioElement.muted = newMutedState;
            console.log('[Room] Remote audio muted:', newMutedState);
        }
        if (audioElement.srcObject) {
            const stream = audioElement.srcObject;
            stream.getAudioTracks().forEach(track => {
                track.enabled = !newMutedState;
                console.log('[Room] Remote track enabled:', track.enabled);
            });
        }
    }, [isRemoteAudioMuted, remoteAudioRef, setIsRemoteAudioMuted]);

    return {initiateCall, endCall, toggleMute, toggleRemoteAudio};
};
